/**
 * Magic Effect —— 制作页入口参数解析（T8，纯函数，可单测）
 *
 * 路由参数约定（对齐 mobile 的 URL 入口，见 design.md 2.1）：
 * - `templateId`（或 `template_id`）  直接指定模板
 * - `animateId`（或 `animate_id`）    经接口四解析出 templateId（解析容错见
 *   `api/magicEffect.ts` 的 `parseTemplateIdFromAnimateParam`，非法 JSON 返回空串）
 * - `directEntry=true`  标记外链直达（仅作语义标记，是否走接口三由「模板在不在
 *   store 列表里」决定 —— 列表里有就用列表数据，避免多余请求与骨架闪烁）
 * - `categoryId` / `traceName` / `groupId` / `searchInfo` / `queryId`  回流上下文
 */
import type {
  MagicEffectTemplate,
  MagicEffectTemplateDetail,
} from '~/api/magicEffect';

export interface GeneratorEntryQuery {
  templateId: string;
  animateId: string;
  categoryId: string;
}

/** URL query 的值可能是 string | string[]（同名参数重复时），统一取第一个字符串 */
const readQueryValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }
  return typeof value === 'string' ? value : '';
};

/**
 * 从路由 query 中读出制作页关心的三个参数（camelCase / snake_case 都认）。
 */
export const readGeneratorEntryQuery = (query: {
  [key: string]: unknown;
}): GeneratorEntryQuery => ({
  templateId:
    readQueryValue(query.templateId) || readQueryValue(query.template_id),
  animateId:
    readQueryValue(query.animateId) || readQueryValue(query.animate_id),
  categoryId: readQueryValue(query.categoryId),
});

/**
 * 把接口三的详情对象归一化成 `MagicEffectTemplate`（轮播数据源的类型）。
 *
 * 接口三字段比接口二少（适配 A11）：缺 `staticCover` / `webpAspectRatio` /
 * `productions` 等，缺的就用「无」语义的兜底值 —— 调用方对缺失字段的展示
 * 规则：无 uses 数据 → 隐藏徽标；无 staticCover → 灰块占位。
 */
export const normalizeTemplateDetail = (
  detail: MagicEffectTemplateDetail,
): MagicEffectTemplate => ({
  ...(detail as unknown as MagicEffectTemplate),
  id: detail.id,
  templateName: detail.templateName ?? '',
  templateWebpUrl: detail.templateWebpUrl ?? '',
  playTypes: detail.playTypes ?? [],
  productions: detail.productions,
});
